import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Save, Plus, Trash2, Music, Play,
  Drum, Clock, RefreshCw, X, FolderOpen,
  Edit, Image, DollarSign, ChevronUp, ChevronDown
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { MediaLibraryPicker } from '@/components/admin/MediaLibraryPicker';

const API = process.env.REACT_APP_BACKEND_URL;

// Available instrument photos from gallery
const availablePhotos = [
  { name: "Surdo 1", url: "https://www.casasamba.com/wp-content/uploads/2026/09/90obs3y2_Surdo-1.jpg" },
  { name: "Three Surdo", url: "https://www.casasamba.com/wp-content/uploads/2026/09/hvbxzby0_Three-Surdo.jpg" },
  { name: "Surdos 3", url: "https://www.casasamba.com/wp-content/uploads/2026/09/fa6ylgln_Surdos-3.jpg" },
  { name: "Surdo Premeira", url: "https://www.casasamba.com/wp-content/uploads/2026/09/vhol8vbh_Surdo-Premeira.jpg" },
  { name: "Pandeiro", url: "https://www.casasamba.com/wp-content/uploads/2026/09/ea6b06e4_Pandeiro.jpg" },
  { name: "Tambourim", url: "https://www.casasamba.com/wp-content/uploads/2026/09/f1zb9ary_Tambourim.jpg" },
  { name: "Timbau", url: "https://www.casasamba.com/wp-content/uploads/2026/09/rtid2h63_Timbau.jpg" },
  { name: "Caixa Repeque 3", url: "https://www.casasamba.com/wp-content/uploads/2026/09/60pr4zhk_caixa_repeque3.jpg" },
  { name: "Caixa Repeque 8", url: "https://www.casasamba.com/wp-content/uploads/2026/09/73zxmtfj_caixa_repeque8.jpg" },
  { name: "Agogo", url: "https://www.casasamba.com/wp-content/uploads/2026/09/x5ukr87e_Agogo.jpg" },
  { name: "Reco-Reco", url: "https://www.casasamba.com/wp-content/uploads/2026/09/c70m1q7s_Reco-Reco.jpg" },
  { name: "Atabaque", url: "https://www.casasamba.com/wp-content/uploads/2026/09/3l9bbv47_atabaque11-2.jpg" },
  { name: "Cuica", url: "https://www.casasamba.com/wp-content/uploads/2026/09/qg1yvxz2_Cuica.jpg" },
  { name: "Berimbau", url: "https://www.casasamba.com/wp-content/uploads/2026/09/vjfgnz4u_berimbau1.jpg" },
  { name: "Platenella", url: "https://www.casasamba.com/wp-content/uploads/2026/09/fjxklurm_Platenella.jpg" },
  { name: "Frying Pan", url: "https://www.casasamba.com/wp-content/uploads/2026/09/ljjuw152_Frying-pan.jpg" },
  { name: "Samba Instruments", url: "https://www.casasamba.com/wp-content/uploads/2026/09/rldhyyfe_Samba-instruments.jpg" },
  { name: "Pagode Instruments", url: "https://www.casasamba.com/wp-content/uploads/2026/09/k41dpwmt_Poagode-Instruments.jpg" },
];

const AdminPlayAlong = () => {
  const navigate = useNavigate();
  
  // Instrument Lessons state (for "Play Along with the Master" / "Choose Your Instrument Lesson")
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showPhotoPicker, setShowPhotoPicker] = useState(false);

  const emptyLesson = {
    id: '',
    name: '',
    price: 24.99,
    image: '',
    video_url: '',
    description: '',
    details: '',
    order: 0
  };

  const [formData, setFormData] = useState(emptyLesson);

  useEffect(() => {
    fetchLessons();
  }, []);

  const fetchLessons = async () => {
    try {
      const res = await fetch(`${API}/api/instrument-lessons`);
      if (res.ok) {
        const data = await res.json();
        setLessons(data);
      } else {
        toast.error('Failed to load lessons');
      }
    } catch (err) {
      console.error('Failed to fetch lessons:', err);
      toast.error('Failed to load lessons');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.image) {
      toast.error('Name and image are required');
      return;
    }

    // Generate ID from name if creating new
    // Remove any MongoDB _id field that might have been included
    const { _id, created_at, updated_at, ...cleanData } = formData;
    const lessonData = {
      ...cleanData,
      id: editingLesson ? cleanData.id : cleanData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
    };

    setSaving(true);
    try {
      const url = editingLesson 
        ? `${API}/api/instrument-lessons/${editingLesson.id}`
        : `${API}/api/instrument-lessons`;
      
      const method = editingLesson ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(lessonData)
      });

      // Check content type before parsing
      const contentType = res.headers.get('content-type');
      let data;
      if (contentType && contentType.includes('application/json')) {
        data = await res.json();
      } else {
        const text = await res.text();
        console.error('Non-JSON response:', text);
        throw new Error('Server returned non-JSON response');
      }
      
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to save');
      }

      toast.success(editingLesson ? 'Lesson updated!' : 'Lesson created!');
      fetchLessons();
      resetForm();
    } catch (err) {
      console.error('Save error:', err);
      toast.error(err.message || 'Failed to save lesson');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this lesson?')) return;

    try {
      const res = await fetch(`${API}/api/instrument-lessons/${id}`, {
        method: 'DELETE'
      });

      if (!res.ok) throw new Error('Failed to delete');

      toast.success('Lesson deleted!');
      fetchLessons();
    } catch (err) {
      console.error('Delete error:', err);
      toast.error('Failed to delete lesson');
    }
  };

  const handleSeedData = async () => {
    if (!confirm('This will reset all instrument lessons to defaults. Continue?')) return;

    try {
      const res = await fetch(`${API}/api/instrument-lessons/seed`, {
        method: 'POST'
      });

      if (!res.ok) throw new Error('Failed to seed data');

      toast.success('Lessons reset to defaults!');
      fetchLessons();
    } catch (err) {
      console.error('Seed error:', err);
      toast.error('Failed to reset lessons');
    }
  };

  const resetForm = () => {
    setFormData(emptyLesson);
    setEditingLesson(null);
    setShowForm(false);
  };

  const handleEdit = (lesson) => {
    setFormData({
      id: lesson.id,
      name: lesson.name,
      price: lesson.price,
      image: lesson.image || '',
      video_url: lesson.video_url || '',
      description: lesson.description || '',
      details: lesson.details || '',
      order: lesson.order || 0
    });
    setEditingLesson(lesson);
    setShowForm(true);
  };

  const selectPhoto = (url) => {
    setFormData({ ...formData, image: url });
    setShowPhotoPicker(false);
  };

  const moveLesson = async (lessonId, direction) => {
    const currentIndex = lessons.findIndex(l => l.id === lessonId);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= lessons.length) return;

    // Get the two lessons to swap
    const currentLesson = lessons[currentIndex];
    const swapLesson = lessons[newIndex];

    try {
      // Update both lessons' order values
      await Promise.all([
        fetch(`${API}/api/instrument-lessons/${currentLesson.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ order: swapLesson.order || newIndex })
        }),
        fetch(`${API}/api/instrument-lessons/${swapLesson.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ order: currentLesson.order || currentIndex })
        })
      ]);

      // Refresh the list
      fetchLessons();
      toast.success('Order updated!');
    } catch (err) {
      console.error('Failed to update order:', err);
      toast.error('Failed to update order');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-900 flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        >
          <Drum className="w-12 h-12 text-primary" />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-900 text-white">
      {/* Header */}
      <div className="bg-stone-800 border-b border-stone-700 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="p-2 hover:bg-stone-700 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold">Play Along Editor</h1>
              <p className="text-sm text-muted-foreground">Manage "Choose Your Instrument Lesson" on the Play Along page</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleSeedData}
              className="px-4 py-2 bg-stone-700 rounded-lg hover:bg-stone-600 transition-colors flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Reset to Defaults
            </button>
            <button
              onClick={() => {
                setFormData(emptyLesson);
                setEditingLesson(null);
                setShowForm(true);
              }}
              className="px-4 py-2 bg-primary text-black font-bold rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Lesson
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Stats & Link */}
        <div className="bg-stone-800 rounded-xl p-4 border border-stone-700 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center">
                <Drum className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{lessons.length}</p>
                <p className="text-sm text-muted-foreground">Instrument Lessons</p>
              </div>
            </div>
            <Link
              to="/samba-lessons"
              target="_blank"
              className="px-4 py-2 bg-stone-700 rounded-lg hover:bg-stone-600 transition-colors text-sm"
            >
              View Live Page
            </Link>
          </div>
        </div>

        {/* Lessons List */}
        <div className="space-y-4">
          {lessons.map((lesson, index) => (
            <motion.div
              key={lesson.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-stone-800 rounded-xl border border-stone-700 overflow-hidden hover:border-stone-600 transition-colors"
            >
              <div className="flex items-center gap-4 p-4">
                {/* Order Controls */}
                <div className="flex flex-col gap-1">
                  <button
                    onClick={() => moveLesson(lesson.id, 'up')}
                    disabled={index === 0}
                    className="p-1 bg-stone-700 rounded hover:bg-stone-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move up"
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => moveLesson(lesson.id, 'down')}
                    disabled={index === lessons.length - 1}
                    className="p-1 bg-stone-700 rounded hover:bg-stone-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move down"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>

                {/* Image */}
                <div className="w-20 h-20 rounded-lg overflow-hidden bg-stone-700 flex-shrink-0">
                  {lesson.image ? (
                    <img loading="lazy" decoding="async" 
                      src={lesson.image} 
                      alt={lesson.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/80x80?text=Image';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Music className="w-8 h-8 text-stone-500" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-lg truncate">{lesson.name}</h3>
                  <p className="text-muted-foreground text-sm line-clamp-2">{lesson.description}</p>
                </div>

                {/* Price */}
                <div className="text-right">
                  <div className="text-xl font-bold text-primary">${lesson.price?.toFixed(2)}</div>
                  <div className="text-xs text-muted-foreground">Order: {lesson.order || 0}</div>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(lesson)}
                    className="p-2 bg-stone-700 rounded-lg hover:bg-stone-600 transition-colors"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(lesson.id)}
                    className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}

          {lessons.length === 0 && (
            <div className="bg-stone-800 rounded-xl border border-stone-700 p-12 text-center">
              <Drum className="w-16 h-16 mx-auto mb-4 text-stone-600" />
              <h3 className="text-xl font-bold mb-2">No Lessons Yet</h3>
              <p className="text-muted-foreground mb-4">Add your first instrument lesson or load sample data</p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={handleSeedData}
                  className="px-4 py-2 bg-stone-700 rounded-lg hover:bg-stone-600 transition-colors"
                >
                  Load Sample Data
                </button>
                <button
                  onClick={() => setShowForm(true)}
                  className="px-4 py-2 bg-primary text-black font-bold rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Add Lesson
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit/Create Modal */}
      {showForm && (
        <div 
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => resetForm()}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-stone-800 rounded-2xl p-6 max-w-2xl w-full border border-stone-700 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold">
                {editingLesson ? 'Edit Lesson' : 'Add New Lesson'}
              </h3>
              <button onClick={resetForm} className="p-1 hover:bg-stone-700 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Image Preview & Picker */}
              <div>
                <label className="block text-sm font-medium mb-2">Lesson Image</label>
                <div className="flex gap-4">
                  <div className="w-32 h-32 rounded-xl overflow-hidden bg-stone-700 flex-shrink-0">
                    {formData.image ? (
                      <img loading="lazy" decoding="async" 
                        src={formData.image} 
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Image className="w-10 h-10 text-stone-500" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <button
                      type="button"
                      onClick={() => setShowPhotoPicker(true)}
                      className="w-full px-4 py-2 bg-stone-700 rounded-lg hover:bg-stone-600 transition-colors mb-2"
                    >
                      Choose from Gallery
                    </button>
                    <input
                      type="text"
                      value={formData.image}
                      onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                      placeholder="Or paste image URL..."
                      className="w-full px-3 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium mb-1">Lesson Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Pandeiro (Brasilian Tambourine)"
                  className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none"
                />
              </div>

              {/* Price & Order */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Price ($) *</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                      className="w-full pl-9 pr-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Display Order</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none"
                  />
                </div>
              </div>

              {/* Video URL */}
              <div>
                <label className="block text-sm font-medium mb-1">
                  <span className="flex items-center gap-2">
                    <Play className="w-4 h-4 text-primary" />
                    Video URL (Vimeo)
                  </span>
                </label>
                <input
                  type="text"
                  value={formData.video_url || ''}
                  onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                  placeholder="https://player.vimeo.com/video/123456789"
                  className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Paste the Vimeo embed URL for the play-along video. Leave empty if no video.
                </p>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium mb-1">Short Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of the instrument and lesson..."
                  rows={3}
                  className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none resize-none"
                />
              </div>

              {/* Details */}
              <div>
                <label className="block text-sm font-medium mb-1">Additional Details</label>
                <textarea
                  value={formData.details}
                  onChange={(e) => setFormData({ ...formData, details: e.target.value })}
                  placeholder="More detailed information shown when expanded..."
                  rows={3}
                  className="w-full px-4 py-2 bg-stone-700 rounded-lg border border-stone-600 focus:border-primary focus:outline-none resize-none"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={resetForm}
                  className="flex-1 py-2 bg-stone-700 rounded-lg hover:bg-stone-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 py-2 bg-primary text-black font-bold rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Saving...' : (editingLesson ? 'Save Changes' : 'Create Lesson')}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Photo Picker Modal */}
      {showPhotoPicker && (
        <div 
          className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4"
          onClick={() => setShowPhotoPicker(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-stone-800 rounded-2xl p-6 max-w-2xl w-full border border-stone-700"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Select Image</h3>
              <button onClick={() => setShowPhotoPicker(false)} className="p-1 hover:bg-stone-700 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-4 gap-3 max-h-[60vh] overflow-y-auto">
              {availablePhotos.map((photo, idx) => (
                <button
                  key={idx}
                  onClick={() => selectPhoto(photo.url)}
                  className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                    formData.image === photo.url 
                      ? 'border-primary ring-2 ring-primary/50' 
                      : 'border-transparent hover:border-stone-500'
                  }`}
                >
                  <img loading="lazy" decoding="async"
                    src={photo.url}
                    alt={photo.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-black/70 px-2 py-1">
                    <p className="text-xs truncate">{photo.name}</p>
                  </div>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminPlayAlong;
